/**
 * The shelf, as something to choose from.
 *
 * `fixture_library`'s bulk button publishes everything missing and
 * decides for itself what to skip.  This is the other need: an operator
 * who edited two files and wants exactly those two replaced, now.
 *
 * Grouped by ring and planet rather than listed flat, because a
 * filename does not say what it is FOR — `learned_chairs.jsx` is
 * meaningless until you know it serves the `learned` world in ring 1 —
 * and because that grouping is the same map the rest of the page is
 * built on, so nobody has to learn a second way of looking at content.
 *
 * The rings fold, and start folded.  The atlas is thirty-odd shelves
 * and the operator came here for one of them; a wall of every file
 * makes them scroll past the answer.  Folded, the modal opens on the
 * shape of the atlas — the rings, and how much each holds — which is
 * the level at which somebody knows where they are going.
 */

import { useMemo, useState } from "react";

import type { ShelfFile } from "./types";
import { PipelineView, shortPipeline } from "./PipelineView";
import { useShelf } from "./useLibrary";
import { Badge, Button, Check, Id, Label, MONO, Modal, Nothing } from "./ui";

/**
 * Ring → planet → files, in the atlas's own order.
 *
 * The ring key carries the PIPELINE when more than one has shelved
 * something, because every pipeline draws its own ``ring_1``: grouping
 * by the ring name alone would merge two pipelines' shelves into one
 * indistinguishable heap, and give two groups the same test id.
 */
function group(files: ShelfFile[]): [string, [string, ShelfFile[]][]][] {
  const pipelines = new Set(files.map((file) => file.pipeline).filter(Boolean));
  const many = pipelines.size > 1;
  const rings = new Map<string, Map<string, ShelfFile[]>>();
  for (const file of files) {
    // The probes that sit outside the atlas keep their own group rather
    // than being hidden: they are real vessels an operator may need to
    // republish, and a browser that silently omitted them would send
    // somebody looking for a file it had decided not to mention.
    const place = file.ring || "outside the atlas";
    const ring = many && file.pipeline ? `${shortPipeline(file.pipeline)} · ${place}` : place;
    const planet = file.planet || "—";
    if (!rings.has(ring)) rings.set(ring, new Map());
    const planets = rings.get(ring)!;
    if (!planets.has(planet)) planets.set(planet, []);
    planets.get(planet)!.push(file);
  }
  return [...rings.entries()].map(([ring, planets]) => [ring, [...planets.entries()]]);
}

export function PublishFiles({
  onClose,
  onChosen,
}: {
  onClose: () => void;
  /** The reader picked these — hand them to the republish flow. */
  onChosen: (filenames: string[]) => void;
}) {
  const shelf = useShelf(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [pipeline, setPipeline] = useState("");

  const files = shelf.data?.files ?? [];
  const searching = search.trim().length > 0;

  // Every pipeline that has shelved a file, and how many each holds.
  // Read off the files rather than a vocabulary because this modal is
  // about what is ON THE SHELF — a pipeline with nothing to publish has
  // nothing to offer here, unlike the unit view where an empty shelf is
  // a real answer about the catalog.
  const pipelines = useMemo(
    () => [...new Set(files.map((file) => file.pipeline).filter(Boolean))].sort(),
    [files],
  );
  const counts = useMemo(() => {
    const tally: Record<string, number> = {};
    for (const file of files) if (file.pipeline) tally[file.pipeline] = (tally[file.pipeline] ?? 0) + 1;
    return tally;
  }, [files]);

  // The pipeline narrows BEFORE the search, and both narrow `shown` —
  // which is what every bulk pick below is taken over.  "Select all"
  // while a pipeline is chosen means the files in front of you, never
  // the whole shelf you cannot see; the same rule the search already
  // follows, for the same reason.
  const shown = useMemo(() => {
    const scoped = pipeline ? files.filter((file) => file.pipeline === pipeline) : files;
    if (!searching) return scoped;
    return scoped.filter((file) =>
      [file.filename, file.ring, file.planet, file.pipeline]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [files, pipeline, search, searching]);

  const groups = useMemo(() => group(shown), [shown]);
  // A search that hid its own matches behind a fold would be a search
  // that answers "nothing here" while holding the answer, so while one
  // is running every group is open regardless of the folds.
  const isOpen = (ring: string) => searching || expanded.has(ring);
  const allOpen = groups.length > 0 && groups.every(([ring]) => expanded.has(ring));

  const toggleRing = (ring: string) =>
    setExpanded((was) => {
      const now = new Set(was);
      if (now.has(ring)) now.delete(ring);
      else now.add(ring);
      return now;
    });

  const toggle = (filename: string, next: boolean) =>
    setPicked((was) => {
      const now = new Set(was);
      if (next) now.add(filename);
      else now.delete(filename);
      return now;
    });

  // Both bulk picks ADD rather than replace, and they are named
  // "select …" rather than "select only …" for that reason: an operator
  // who has ticked one edited file and then asks for the unpublished
  // ones wants both, and a replace would silently drop the choice they
  // had already made.  "clear" is what takes a selection back down.
  const pickAll = (batch: ShelfFile[]) =>
    setPicked((was) => new Set([...was, ...batch.map((file) => file.filename)]));

  // Over what is SHOWN, so a running search narrows them the way it
  // narrows everything else — "select all" while searching means the
  // matches in front of you, never the whole shelf you cannot see.
  const unpublished = useMemo(() => shown.filter((file) => !file.published), [shown]);
  const addsAll = shown.filter((file) => !picked.has(file.filename)).length;
  const addsUnpublished = unpublished.filter((file) => !picked.has(file.filename)).length;

  return (
    <Modal
      testId="publish-files"
      title="Publish files from the shelf"
      onClose={onClose}
      footer={
        <>
          <span
            data-testid="publish-selected-count"
            style={{ flex: 1, fontSize: 12, color: "var(--color-text-muted)" }}
          >
            {picked.size} selected
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            testId="publish-files-continue"
            kind="primary"
            disabled={picked.size === 0}
            onClick={() => onChosen([...picked])}
          >
            Purge &amp; republish…
          </Button>
        </>
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <PipelineView
          testId="publish-pipeline"
          label="shelf"
          value={pipeline}
          pipelines={pipelines}
          counts={counts}
          onChange={setPipeline}
        />
        <input
          data-testid="publish-files-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="search file, ring, planet…"
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 32,
            padding: "5px 10px",
            borderRadius: 8,
            border: "1px solid var(--color-border)",
            background: "var(--color-bg-subtle)",
            color: "var(--color-text)",
            fontFamily: "inherit",
            fontSize: 13,
            boxSizing: "border-box",
          }}
        />
        <Button
          testId="publish-expand-all"
          // Dead until there is something to unfold.  The shelf arrives
          // a moment after the modal does, and a live button over an
          // empty list reads as broken: it takes the press, unfolds the
          // nothing it can see, and the rings that land a beat later are
          // still shut.
          disabled={searching || groups.length === 0}
          title={
            groups.length === 0
              ? "the shelf is still loading"
              : searching
                ? "every match is shown while a search is running"
                : allOpen
                  ? "fold every ring"
                  : "unfold every ring"
          }
          onClick={() =>
            setExpanded(allOpen ? new Set() : new Set(groups.map(([ring]) => ring)))
          }
        >
          {allOpen ? "collapse all" : "expand all"}
        </Button>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Label style={{ flexShrink: 0 }}>select</Label>
        <Button
          testId="publish-select-all"
          disabled={addsAll === 0}
          title={
            searching ? "the files matching this search" : "every file on the shelf"
          }
          onClick={() => pickAll(shown)}
        >
          all ({shown.length})
        </Button>
        <Button
          testId="publish-select-unpublished"
          disabled={addsUnpublished === 0}
          title="the files the registry has never published — a plain publish, nothing to purge"
          onClick={() => pickAll(unpublished)}
        >
          unpublished ({unpublished.length})
        </Button>
        {picked.size ? (
          <Button testId="publish-select-none" onClick={() => setPicked(new Set())}>
            clear
          </Button>
        ) : null}
        {searching ? (
          <span style={{ fontSize: 11.5, color: "var(--color-text-muted)" }}>
            of the {shown.length} matching this search
          </span>
        ) : null}
      </div>

      {shelf.status === "loading" && !shelf.data ? (
        <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>reading the shelf…</div>
      ) : null}
      {shelf.failure ? (
        <div style={{ color: "var(--color-error)", fontSize: 13 }}>
          {shelf.failure.kind === "error" ? shelf.failure.detail : "you may not read the shelf"}
        </div>
      ) : null}
      {shelf.data && shown.length === 0 ? <Nothing>No file matches that.</Nothing> : null}

      <div style={{ display: "grid", gap: 6 }}>
        {groups.map(([ring, planets]) => {
          const inRing = planets.flatMap(([, entries]) => entries);
          const chosen = inRing.filter((file) => picked.has(file.filename)).length;
          const open = isOpen(ring);
          return (
            <div key={ring}>
              <button
                type="button"
                data-testid={`publish-ring-${ring}`}
                aria-expanded={open}
                onClick={() => toggleRing(ring)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  minHeight: 34,
                  padding: "5px 8px",
                  borderRadius: 8,
                  border: "1px solid var(--color-border-subtle)",
                  background: open ? "var(--color-bg-subtle)" : "transparent",
                  color: "var(--color-text)",
                  cursor: searching ? "default" : "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                  boxSizing: "border-box",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    width: 10,
                    color: "var(--color-text-muted)",
                    transform: open ? "rotate(90deg)" : "none",
                    transition: "transform 120ms ease",
                  }}
                >
                  ▸
                </span>
                <Label style={{ flex: 1 }}>{ring}</Label>
                {/* Selections made inside a ring survive folding it, so
                    the count has to survive too — otherwise the footer
                    says "3 selected" and nothing on screen accounts for
                    them. */}
                {chosen ? <Badge tone="accent">{chosen} selected</Badge> : null}
                <span style={{ fontSize: 11.5, color: "var(--color-text-muted)" }}>
                  {inRing.length} file{inRing.length === 1 ? "" : "s"}
                </span>
              </button>

              {open ? (
                <div
                  data-testid={`publish-ring-files-${ring}`}
                  style={{ display: "grid", gap: 8, margin: "6px 0 10px", paddingLeft: 18 }}
                >
                  {planets.map(([planet, entries]) => (
                    <div key={planet}>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--color-text-secondary)",
                          marginBottom: 2,
                        }}
                      >
                        {planet}
                      </div>
                      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {entries.map((file) => (
                          <li key={file.filename}>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                minHeight: 30,
                                padding: "3px 6px",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                            >
                              <Check
                                testId={`publish-file-${file.filename}`}
                                checked={picked.has(file.filename)}
                                onChange={(next) => toggle(file.filename, next)}
                                label={file.filename}
                              />
                              <Id style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                                {file.filename}
                              </Id>
                              {file.published ? (
                                <Badge
                                  tone="neutral"
                                  title={`${file.itemIds.length} registry row(s)`}
                                >
                                  published · {file.itemIds.length}
                                </Badge>
                              ) : (
                                <Badge tone="warn">not published</Badge>
                              )}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, fontSize: 12, color: "var(--color-text-muted)", fontFamily: MONO }}>
        A file already published is purged first and then published again from disk; one that is
        not is simply published.
      </div>
    </Modal>
  );
}
